import { CollapseWithPill } from '@components/display';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useEffect, useState } from 'react';
import { ProjectType } from '@pages/types';
import {
  getAiGenerativeResource,
  GetAiGenerativeResourcePayloadType,
  UpdatePrefillProjectPayloadType,
} from '@api';
import { LoadingOutlined } from '@ant-design/icons';
import { AiGenerativeResourceApiType } from '@api/types';
import { FormItem, SelectInput, Space } from 'common-components';
import { PrefillMergeType, PrefillType } from '@definitions';
import { getStateKeyByValue } from '@utils';

type PrefillSettingsCollapsePropsType = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  onChange: (prefillSettings: ProjectType['prefillSettings']) => void
};

type AiGenerativeType = {
  all: AiGenerativeResourceApiType[]
  chosen?: AiGenerativeResourceApiType
  isLoading: boolean
  optionsSelect: {
    label: string
    value: string
  }[]
};

const PrefillSettingsCollapse = ({
  isOpen,
  onChange,
  setIsOpen,
}: PrefillSettingsCollapsePropsType) => {
  const { translate } = useAppTranslation();

  const prefillingTypeOptions = [
    {
      label: translate('project.prefillTypes.localGenerativeAi'),
      value: getStateKeyByValue<PrefillType>(PrefillMergeType, 'iagenerative'),
    },
    { label: 'GPT', value: getStateKeyByValue<PrefillType>(PrefillMergeType, 'gpt') },
    {
      label: translate('project.prefillTypes.manual'),
      value: getStateKeyByValue<PrefillType>(PrefillMergeType, 'manuel'),
    },
  ];

  const [statePrefillingType, setStatePrefillingType] = useState<PrefillType>(0);
  const [aiGenerative, setAiGenerative] = useState<AiGenerativeType>({
    all: [],
    isLoading: false,
    optionsSelect: [],
  });

  useEffect(() => {
    let aiGenerativeCurrent: UpdatePrefillProjectPayloadType['prefillSettings']['aiGenerative'];

    if ((statePrefillingType === 1 || statePrefillingType === 3) && aiGenerative?.chosen) {
      aiGenerativeCurrent = aiGenerative?.chosen;
    }
    onChange({ type: statePrefillingType, aiGenerative: aiGenerativeCurrent });
  }, [statePrefillingType, JSON.stringify(aiGenerative.chosen)]);

  const prefillingTypeSelectionHandler = async (e: string) => {
    try {
      const state = Number(e) as PrefillType;
      setStatePrefillingType(state);
      if (state === 1 || state === 3) {
        await aiGenerativeSelectionHandler(state);
      } else {
        setAiGenerative({
          all: [],
          chosen: undefined,
          isLoading: false,
          optionsSelect: [],
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const aiGenerativeSelectionHandler = async (type: GetAiGenerativeResourcePayloadType['type']) => {
    try {
      setAiGenerative({
        all: [],
        chosen: undefined,
        isLoading: true,
        optionsSelect: [],
      });
      const response = await getAiGenerativeResource({
        type,
      });
      const options = response?.map((item) => ({
        label: item.name,
        value: item.idAIGenerative,
      }));
      setAiGenerative({ all: response, isLoading: false, optionsSelect: options });
    } catch (e) {
      setAiGenerative({
        all: [],
        chosen: undefined,
        isLoading: false,
        optionsSelect: [],
      });
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const prefillSettingsHandler = (e: string) => {
    try {
      const chosenPrefillSettings = aiGenerative.all?.find((item) => item.idAIGenerative === e);
      setAiGenerative((prevState) => ({ ...prevState, chosen: chosenPrefillSettings }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const Loader = () => (
    <div
      style={{
        marginTop: '10px',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      <LoadingOutlined style={{ fontSize: '20px' }} />
    </div>
  );

  const PrefillTypePanel = {
    title: translate('projectCreatePage.collapse.artificialModel.title'),
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    modifEnabled: true,
    content: (
      <Space orientation="vertical" style={{ width: '100%', gap: 8, paddingBottom: 16 }}>
        <Space orientation="vertical" style={{ width: '100%', padding: '0 24px', gap: 16 }}>
          <FormItem label={translate('project.prefillType')}>
            <SelectInput
              options={prefillingTypeOptions}
              onChange={prefillingTypeSelectionHandler}
            />
          </FormItem>
          {(statePrefillingType === 1 || statePrefillingType === 3) && aiGenerative.isLoading && (
            <Loader />
          )}
          {(statePrefillingType === 1 || statePrefillingType === 3) &&
            !!aiGenerative.optionsSelect.length && (
            <FormItem
              label={
                statePrefillingType === 1
                  ? translate('project.modelSelection')
                  : translate('project.versionSelection')
              }
            >
              <SelectInput
                options={aiGenerative.optionsSelect}
                onChange={prefillSettingsHandler}
              />
            </FormItem>
          )}
          {/* TODO US 7674: wire the AI settings here — see FormFieldsModel.tsx */}
        </Space>
      </Space>
    ),
  };

  return <CollapseWithPill panels={[PrefillTypePanel]} />;
};

export default PrefillSettingsCollapse;
